/**
 * PanelContext
 * Tracks visibility of the three v3 chrome panels: left nav, bottom tabs, right tray.
 * Spec §1.9: persisted fields (leftNavState, bottomVisible, rightVisible) live
 * in localStorage and survive navigation.
 *
 * The left panel has two independent axes:
 *
 * - Desktop: `leftNavState` tri-state cycle (expanded → collapsed → hidden →
 *   expanded) per spec §1.2. Persisted. Dispatched via `cycleLeft`.
 * - Mobile (§3.1): `leftMobileOverlayOpen` boolean. TRANSIENT (not persisted)
 *   so a prior desktop session doesn't auto-open the full-screen sheet on
 *   fresh mobile mounts. Dispatched via `toggleLeftMobile`; dismissed via
 *   `closeMobileOverlay`.
 *
 * ScrollableTitleRow's left-panel toggle reads `useIsMobile()` and dispatches
 * the appropriate action for the active viewport.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import type { LeftNavState } from '../components/navigation/LeftNav';

const LEFT_NAV_CYCLE: LeftNavState[] = ['expanded', 'collapsed', 'hidden'];

export interface PanelState {
  leftNavState: LeftNavState;
  leftVisible: boolean; // derived: leftNavState !== 'hidden'
  /**
   * Transient flag for the mobile LeftNav overlay (§3.1). Separate from
   * the desktop tri-state so a persisted `leftNavState='expanded'` from
   * a previous desktop session does not auto-open the full-screen sheet
   * on mobile. Defaults to false; flipped by `toggleLeftMobile()`,
   * cleared by `closeMobileOverlay()` (backdrop/Escape/close-button).
   */
  leftMobileOverlayOpen: boolean;
  bottomVisible: boolean;
  rightVisible: boolean;
  cycleLeft: () => void;
  /**
   * Binary toggle for the mobile LeftNav overlay. Consumers should
   * dispatch this on mobile instead of `cycleLeft` (the desktop
   * tri-state has no meaningful mobile rendering).
   */
  toggleLeftMobile: () => void;
  /**
   * Dismiss the mobile LeftNav overlay (§3.1). Wired to the overlay's
   * backdrop tap, Escape handler, and explicit close button. Does NOT
   * touch `leftNavState` — persisting a desktop-hidden side effect
   * from a mobile phone session would leave the user's desktop nav
   * collapsed on their next desktop visit.
   */
  closeMobileOverlay: () => void;
  toggleBottom: () => void;
  toggleRight: () => void;
}

const PanelContext = createContext<PanelState | null>(null);

const STORAGE_KEY = 'v3-panel-state';

interface StoredPanelState {
  leftNavState: LeftNavState;
  bottomVisible: boolean;
  rightVisible: boolean;
}

function loadState(): StoredPanelState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { leftNavState: 'expanded', bottomVisible: true, rightVisible: false };
    const parsed = JSON.parse(raw) as Partial<StoredPanelState>;
    return {
      leftNavState: parsed.leftNavState ?? 'expanded',
      bottomVisible: parsed.bottomVisible ?? true,
      rightVisible: parsed.rightVisible ?? false,
    };
  } catch {
    return { leftNavState: 'expanded', bottomVisible: true, rightVisible: false };
  }
}

function saveState(state: StoredPanelState) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // ignore quota / privacy mode errors
  }
}

export function PanelProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<StoredPanelState>(() => loadState());
  // Transient: NOT persisted — the mobile overlay always starts closed
  // on a fresh mount, regardless of what the user did in their previous
  // session. That matches mobile-app conventions (nothing pops open on
  // launch) and avoids reading stale desktop state.
  const [leftMobileOverlayOpen, setLeftMobileOverlayOpen] = useState(false);

  useEffect(() => {
    saveState(state);
  }, [state]);

  const cycleLeft = useCallback(
    () =>
      setState((prev) => {
        const idx = LEFT_NAV_CYCLE.indexOf(prev.leftNavState);
        const next = LEFT_NAV_CYCLE[(idx + 1) % LEFT_NAV_CYCLE.length];
        return { ...prev, leftNavState: next };
      }),
    [],
  );
  const toggleLeftMobile = useCallback(
    () => setLeftMobileOverlayOpen((v) => !v),
    [],
  );
  const closeMobileOverlay = useCallback(() => {
    setLeftMobileOverlayOpen(false);
  }, []);
  const toggleBottom = useCallback(
    () => setState((prev) => ({ ...prev, bottomVisible: !prev.bottomVisible })),
    [],
  );
  const toggleRight = useCallback(
    () => setState((prev) => ({ ...prev, rightVisible: !prev.rightVisible })),
    [],
  );

  const value = useMemo<PanelState>(
    () => ({
      leftNavState: state.leftNavState,
      leftVisible: state.leftNavState !== 'hidden',
      leftMobileOverlayOpen,
      bottomVisible: state.bottomVisible,
      rightVisible: state.rightVisible,
      cycleLeft,
      toggleLeftMobile,
      closeMobileOverlay,
      toggleBottom,
      toggleRight,
    }),
    [state, leftMobileOverlayOpen, cycleLeft, toggleLeftMobile, closeMobileOverlay, toggleBottom, toggleRight],
  );

  return <PanelContext.Provider value={value}>{children}</PanelContext.Provider>;
}

export function usePanels(): PanelState {
  const ctx = useContext(PanelContext);
  if (!ctx) throw new Error('usePanels must be used within a PanelProvider');
  return ctx;
}
